/**
 * CsvImport - Reusable modal component for bulk CSV import of entities.
 *
 * Provides a multi-step workflow for importing Users, Companies, Locations,
 * or Contacts:
 *	 1. File Selection — user picks a .csv file via a file picker
 *	 2. Preview — shows the first 5 rows in a table plus detected column mapping
 *	 3. Upload — sends the file to the backend import endpoint
 *	 4. Results — displays import summary (imported, skipped, errors, duplicates)
 *
 * Features:
 * - Drag-and-drop file picker that accepts .csv files only
 * - Preview table showing the first 5 data rows after file selection
 * - Column mapping display comparing detected columns vs expected columns
 * - "Download Template" link for generating a sample CSV with correct headers
 * - Results view with color-coded rows (green=imported, yellow=duplicate, red=error)
 * - Loading state with spinner during upload
 *
 * Uses the Modal organism for consistent overlay styling and behavior.
 *
 * @example
 * <CsvImport
 *	   isOpen={showImport}
 *	   onClose={() => setShowImport(false)}
 *	   entityType="users"
 *	   onImportComplete={(result) => console.log(result)}
 * />
 */
import React, { useState, useCallback, useRef } from 'react';
import { Modal } from '../Modal';
import { Button, LinkButton } from '../../atoms';
import { importService, IMPORT_TEMPLATE_HEADERS } from '../../../services/importService';
import type { ImportResult, ImportEntityType } from '../../../services/importService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/* The entity-type union is owned by importService — the service layer keys
   both its upload dispatch and its CSV templates by it. Re-exported here so
   consumers can keep importing it alongside the component. */
export type { ImportEntityType };

/**
 * Props for the CsvImport component.
 *
 * @property isOpen - Controls whether the import modal is visible
 * @property onClose - Callback fired when the user dismisses the modal
 * @property entityType - Which entity to import: 'users', 'companies', 'locations', or 'contacts'
 * @property onImportComplete - Callback fired after a successful import with the result summary
 */
export interface CsvImportProps {
	isOpen: boolean;
	onClose: () => void;
	entityType: ImportEntityType;
	onImportComplete: (result: ImportResult) => void;
}

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Expected CSV column headers for each entity type, used for column mapping
	display. Shared with the downloadable template so the columns the user is
	told to supply are exactly the ones the mapping check looks for. */
const EXPECTED_COLUMNS = IMPORT_TEMPLATE_HEADERS;

/** Human-readable labels for each entity type */
const ENTITY_LABELS: Record<ImportEntityType, string> = {
	users: 'Users',
	companies: 'Companies',
	locations: 'Locations',
	contacts: 'Contacts',
};

/** Maps each entity type to the importService method that uploads it.
	Keyed lookup rather than an if/else chain so adding an entity type is a
	single-line change here plus the service method it points at. */
const UPLOAD_BY_ENTITY: Record<ImportEntityType, (file: File) => Promise<ImportResult>> = {
	users: importService.importUsers,
	companies: importService.importCompanies,
	locations: importService.importLocations,
	contacts: importService.importContacts,
};

/** The import workflow steps — tracks UI state progression */
type ImportStep = 'select' | 'preview' | 'uploading' | 'results';

/* ============================================
 * COMPONENT
 * ============================================ */

const CsvImport: React.FC<CsvImportProps> = ({
	isOpen,
	onClose,
	entityType,
	onImportComplete,
}) => {
	/* --- Component state --- */

	/** The selected CSV file (null before a file is chosen) */
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	/** Current step in the import workflow */
	const [step, setStep] = useState<ImportStep>('select');

	/** Parsed CSV headers from the selected file */
	const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);

	/** First 5 data rows parsed from the selected file for preview */
	const [previewRows, setPreviewRows] = useState<string[][]>([]);

	/** Import result summary returned by the backend */
	const [importResult, setImportResult] = useState<ImportResult | null>(null);

	/** Error message to display when something goes wrong */
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	/** Ref for the hidden file input element */
	const fileInputRef = useRef<HTMLInputElement>(null);

	/* ============================================
	 * FILE PARSING
	 * ============================================ */

	/**
	 * Parses the selected CSV file client-side to extract headers and
	 * the first 5 data rows for the preview step. Uses the FileReader
	 * API to read the file as text, then splits by newlines and commas.
	 *
	 * @param file - The CSV File object selected by the user
	 */
	const parseFileForPreview = useCallback((file: File) => {
		const reader = new FileReader();

		reader.onload = (e) => {
			// Read the file content as a string
			const text = e.target?.result as string;

			// Split into lines and filter out completely empty lines
			const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');

			if (lines.length === 0) {
				setErrorMessage('The selected CSV file is empty.');
				return;
			}

			// First line is the header row — parse into individual column names
			const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
			setParsedHeaders(headers);

			// Extract up to 5 data rows for the preview table
			const dataRows = lines.slice(1, 6).map((line) => {
				// Simple CSV parsing — split by comma (handles basic CSVs)
				return line.split(',').map((cell) => cell.trim());
			});
			setPreviewRows(dataRows);

			// Advance to the preview step
			setStep('preview');
			setErrorMessage(null);
		};

		reader.onerror = () => {
			setErrorMessage('Failed to read the selected file. Please try again.');
		};

		// Read the file as UTF-8 text
		reader.readAsText(file);
	}, []);

	/* ============================================
	 * EVENT HANDLERS
	 * ============================================ */

	/**
	 * Handles file selection from the file input element.
	 * Validates the file type and triggers preview parsing.
	 */
	const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Validate file extension — must be .csv
		if (!file.name.toLowerCase().endsWith('.csv')) {
			setErrorMessage('Please select a .csv file.');
			return;
		}

		setSelectedFile(file);
		setErrorMessage(null);
		parseFileForPreview(file);
	}, [parseFileForPreview]);

	/**
	 * Opens the native file picker dialog by clicking the hidden input.
	 */
	const handleBrowseClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	/**
	 * Sends the selected CSV file to the backend import endpoint.
	 * Transitions through uploading → results steps and calls
	 * the onImportComplete callback when finished.
	 */
	const handleImport = useCallback(async () => {
		if (!selectedFile) return;

		// Transition to the uploading state (shows spinner)
		setStep('uploading');
		setErrorMessage(null);

		try {
			// Call the appropriate import service method based on entity type
			const result = await UPLOAD_BY_ENTITY[entityType](selectedFile);

			// Store the result and transition to the results view
			setImportResult(result);
			setStep('results');

			// Notify the parent component so it can refresh data
			onImportComplete(result);
		} catch (err: any) {
			// Show the error and revert to the preview step
			setErrorMessage(err?.message || 'Import failed. Please try again.');
			setStep('preview');
		}
	}, [selectedFile, entityType, onImportComplete]);

	/**
	 * Downloads the CSV template for the current entity type.
	 */
	const handleDownloadTemplate = useCallback(() => {
		importService.downloadTemplate(entityType);
	}, [entityType]);

	/**
	 * Resets all state to allow the user to start a new import.
	 */
	const handleReset = useCallback(() => {
		setSelectedFile(null);
		setStep('select');
		setParsedHeaders([]);
		setPreviewRows([]);
		setImportResult(null);
		setErrorMessage(null);
		// Reset the file input so the same file can be re-selected
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	}, []);

	/**
	 * Closes the modal and resets state for a clean next open.
	 */
	const handleClose = useCallback(() => {
		handleReset();
		onClose();
	}, [handleReset, onClose]);

	/* ============================================
	 * RENDER HELPERS
	 * ============================================ */

	/** The expected columns for the current entity type */
	const expectedCols = EXPECTED_COLUMNS[entityType];

	/**
	 * Renders the column mapping comparison between detected and expected columns.
	 * Shows a green checkmark for matched columns and a red X for missing ones.
	 */
	const renderColumnMapping = () => (
		<div className="mt-12px p-12px bg-brand-surface rounded-md">
			<p className="text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">Column Mapping</p>
			<div className="csv-import__mapping-grid grid grid-cols-2 gap-4px text-12px">
				{/* Header row for the mapping table */}
				<span className="font-medium text-brand-text-muted">Expected</span>
				<span className="font-medium text-brand-text-muted">Detected</span>
				{/* One row per expected column — show match status */}
				{expectedCols.map((col) => {
					/* Check if the detected headers include this expected column */
					const isMatched = parsedHeaders.includes(col);
					return (
						<React.Fragment key={col}>
							<span className="text-brand-text">{col}</span>
							<span className={isMatched ? 'text-green-700' : 'text-red-600'}>{isMatched ? col + ' \u2713' : 'Missing \u2717'}</span>
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);

	/**
	 * Renders the file selection step with a file picker area and
	 * a download template link.
	 */
	const renderSelectStep = () => (
		<div className="space-y-16px">
			{/* File picker area — styled as a dashed-border drop zone */}
			<div className="csv-import__dropzone border-2 border-dashed border-brand-border rounded-lg p-20px tablet:p-32px text-center">
				{/* Hidden native file input — triggered by the Browse button */}
				<input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
				{/* Upload icon */}
				<svg className="mx-auto h-40px w-40px text-brand-text-subtle mb-12px" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
				</svg>
				<p className="text-14px text-brand-text-muted mb-8px">Select a CSV file to import {ENTITY_LABELS[entityType].toLowerCase()}</p>
				<Button variant="primary" size="sm" onClick={handleBrowseClick}>Browse Files</Button>
			</div>

			{/* Download template link */}
			<div className="text-center">
				<LinkButton onClick={handleDownloadTemplate} underline>Download CSV Template</LinkButton>
			</div>
		</div>
	);

	/**
	 * Renders the preview step showing parsed data and column mapping.
	 * Includes the first 5 rows in a scrollable table and action buttons.
	 */
	const renderPreviewStep = () => (
		<div className="space-y-12px">
			{/* File info badge. The name is the side that gives way: exported
			    CSVs come with names like gbrands-contacts-2026-09-04-final.csv,
			    and without min-w-0 + truncate that pushes "Change file" off the
			    right edge of a phone rather than eliding. */}
			<div className="csv-import__file flex items-center justify-between gap-8px min-w-0">
				<div className="flex items-center gap-8px min-w-0">
					<svg className="h-16px w-16px shrink-0 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
					</svg>
					<span className="text-14px text-brand-text font-medium truncate">{selectedFile?.name}</span>
				</div>
				<button type="button" onClick={handleReset} className="inline-flex items-center min-h-44px shrink-0 text-12px text-brand-primary hover:text-brand-primary-dark transition-colors">Change file</button>
			</div>

			{/* Preview table showing first 5 rows */}
			<div className="csv-import__preview border border-brand-border rounded-md overflow-x-auto">
				<table className="w-full text-12px text-left">
					<thead className="bg-brand-surface border-b border-brand-border">
						<tr>
							{parsedHeaders.map((header) => (
								<th key={header} className="px-8px py-6px font-medium text-brand-text-muted whitespace-nowrap">{header}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{previewRows.map((row, rowIdx) => (
							<tr key={rowIdx} className="border-b border-brand-border/50">
								{row.map((cell, cellIdx) => (
									<td key={cellIdx} className="px-8px py-4px text-brand-text whitespace-nowrap max-w-[120px] truncate">{cell}</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Row count indicator */}
			<p className="text-12px text-brand-text-subtle">Showing first {previewRows.length} row(s) from the file.</p>

			{/* Column mapping comparison */}
			{renderColumnMapping()}

			{/* Action buttons */}
			<div className="csv-import__actions flex flex-wrap justify-end gap-8px pt-8px">
				<Button variant="quiet" size="sm" onClick={handleClose}>Cancel</Button>
				<Button variant="primary" size="sm" onClick={handleImport}>Import</Button>
			</div>
		</div>
	);

	/**
	 * Renders the uploading state with a loading spinner.
	 */
	const renderUploadingStep = () => (
		<div className="csv-import__progress flex flex-col items-center justify-center py-32px">
			{/* Spinner animation */}
			<div className="animate-spin rounded-full h-32px w-32px border-4 border-brand-primary border-t-transparent mb-16px" />
			<p className="text-14px text-brand-text-muted">Importing {ENTITY_LABELS[entityType].toLowerCase()}...</p>
			<p className="text-12px text-brand-text-subtle mt-4px">This may take a moment for large files.</p>
		</div>
	);

	/**
	 * Renders the results step showing the import summary with counts
	 * and detail rows for duplicates and errors.
	 */
	const renderResultsStep = () => {
		if (!importResult) return null;

		return (
			<div className="space-y-16px">
				{/* Summary counts in a grid layout */}
				<div className="csv-import__summary grid grid-cols-1 tablet:grid-cols-3 gap-12px">
					{/* Imported count — green */}
					<div className="bg-green-50 border border-green-200 rounded-md p-12px text-center">
						<p className="text-24px font-bold text-green-800">{importResult.imported}</p>
						<p className="text-12px text-green-700 mt-4px">Imported</p>
					</div>
					{/* Skipped count — yellow */}
					<div className="bg-yellow-50 border border-yellow-200 rounded-md p-12px text-center">
						<p className="text-24px font-bold text-yellow-800">{importResult.skipped}</p>
						<p className="text-12px text-yellow-700 mt-4px">Skipped (duplicates)</p>
					</div>
					{/* Errors count — red */}
					<div className="bg-red-50 border border-red-200 rounded-md p-12px text-center">
						<p className="text-24px font-bold text-red-800">{importResult.errors.length}</p>
						<p className="text-12px text-red-700 mt-4px">Errors</p>
					</div>
				</div>

				{/* Duplicate rows detail — highlighted in yellow */}
				{importResult.duplicates.length > 0 && (
					<div>
						<p className="text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">Duplicates (Skipped)</p>
						<div className="space-y-4px">
							{importResult.duplicates.map((dup, idx) => (
								<div key={idx} className="flex items-start gap-8px px-12px py-8px bg-yellow-50 border border-yellow-200 rounded-md text-12px">
									<span className="font-medium text-yellow-800 whitespace-nowrap">Row {dup.row}:</span>
									<span className="text-yellow-700">{dup.email || dup.name || 'Duplicate record'} already exists.</span>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Error rows detail — highlighted in red */}
				{importResult.errors.length > 0 && (
					<div>
						<p className="text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">Errors</p>
						<div className="space-y-4px">
							{importResult.errors.map((err, idx) => (
								<div key={idx} className="flex items-start gap-8px px-12px py-8px bg-red-50 border border-red-200 rounded-md text-12px">
									<span className="font-medium text-red-800 whitespace-nowrap">Row {err.row}:</span>
									<span className="text-red-700">{err.message}</span>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Action buttons — import another file or close */}
				<div className="csv-import__actions flex flex-wrap justify-end gap-8px pt-8px">
					<Button variant="quiet" size="sm" onClick={handleReset}>Import Another</Button>
					<Button variant="primary" size="sm" onClick={handleClose}>Done</Button>
				</div>
			</div>
		);
	};

	/* ============================================
	 * MAIN RENDER
	 * ============================================ */

	return (
		<Modal isOpen={isOpen} onClose={handleClose} title={`Import ${ENTITY_LABELS[entityType]}`} size="lg" className="csv-import" dataRef="organism-csv-import">
			{/* Error message banner — shown when file parsing or upload fails */}
			{errorMessage && (
				<div className="csv-import__error mb-12px px-12px py-8px rounded-md text-14px font-medium bg-red-50 text-red-800 border border-red-200">
					{errorMessage}
				</div>
			)}

			{/* Render the current workflow step */}
			{step === 'select' && renderSelectStep()}
			{step === 'preview' && renderPreviewStep()}
			{step === 'uploading' && renderUploadingStep()}
			{step === 'results' && renderResultsStep()}
		</Modal>
	);
};

export default CsvImport;
